import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { configRouteRedirect } from '~/lib/config-access';
import { useMemo, useState } from 'react';
import {
  Building2,
  Plus,
  Settings2,
  Globe,
  ShieldAlert,
  CheckCircle2,
  CircleDashed,
  X,
  ArrowLeft,
  ArrowRight,
  Copy,
  KeyRound,
  UserPlus,
  RotateCcw,
  LogIn,
  Info,
  Search,
} from 'lucide-react';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { SecretInput } from '~/components/SecretInput/SecretInput';
import { currentUser } from '~/lib/pb';
import {
  useCreateTenantWithAdmin,
  useTenants,
  useUpdateTenant,
  type CreateTenantWithAdminResult,
  type FirstAdminInput,
  type TenantInput,
} from '~/lib/tenants-queries';
import { useTenantContext } from '~/lib/tenantContext';
import { PLAN_ORDER, PLAN_LABELS, REGION_ORDER, REGION_LABELS, REGION_CURRENCY, TIMEZONE_OPTIONS, defaultTimezone, ensureCompletoOption, presetCapabilities, tenantHasCapability, CAPABILITY_KEYS, CAPABILITY_LABELS, CUSTOM_PLAN_SLUG, planDisplayLabel } from '~/lib/capabilities';
import { CapabilityPicker } from '~/components/CapabilityPicker/CapabilityPicker';
import { useActivePlans, type Plan } from '~/lib/plans-queries';
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from '~/lib/permissions';
import type { Tenant } from '~/lib/types';
import styles from './_authed.clientes.module.css';

export const Route = createFileRoute('/_authed/clientes')({
  beforeLoad: () => {
    const dest = configRouteRedirect(currentUser()?.role, '/clientes');
    if (dest) throw redirect(dest);
  },
  component: TenantsPage,
});

// ── Tipos de cliente (buckets para las pestañas de filtro) ────────────
//
// Derivados de capabilities, NO del slug del plan (las capacidades son la
// fuente de verdad). Tres buckets: clientes con CRM completo (Sistema Focus),
// clientes solo de sitio web (Web/CMS), y el resto (bandeja/captura sin sitio).

type TenantType = 'crm' | 'cms' | 'otros';

function tenantType(t: Tenant): TenantType {
  if (tenantHasCapability(t, 'crm')) return 'crm';
  if (tenantHasCapability(t, 'cms')) return 'cms';
  return 'otros';
}

type TypeFilter = 'todos' | TenantType;

const TYPE_TABS: Array<{ id: TypeFilter; label: string }> = [
  { id: 'todos', label: 'Todos' },
  { id: 'crm', label: 'Sistema Focus' },
  { id: 'cms', label: 'Web / CMS' },
  { id: 'otros', label: 'Otros' },
];

function TenantsPage() {
  const me = currentUser();
  const isSuperadmin = me?.role === 'superadmin';
  const tenantsQ = useTenants();
  const plansQ = useActivePlans();
  const plans = plansQ.data;
  const navigate = useNavigate();
  const { enterTenantView } = useTenantContext();
  const [creating, setCreating] = useState(false);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('todos');
  const [query, setQuery] = useState('');

  const allTenants = tenantsQ.data ?? [];

  // Conteo por tipo (para el badge de cada pestaña).
  const counts = useMemo(() => {
    const c: Record<TypeFilter, number> = { todos: allTenants.length, crm: 0, cms: 0, otros: 0 };
    for (const t of allTenants) c[tenantType(t)]++;
    return c;
  }, [allTenants]);

  // Lista visible: filtro por tipo + búsqueda (nombre / slug / dominio).
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTenants.filter((t) => {
      if (typeFilter !== 'todos' && tenantType(t) !== typeFilter) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q) ||
        (t.domain ?? '').toLowerCase().includes(q)
      );
    });
  }, [allTenants, typeFilter, query]);

  // Helper: label vivo desde PB con fallback al hardcoded (incl. Custom).
  const planLabel = (slug: string): string => planDisplayLabel(slug, plans);

  function handleEnterTenant(tenantId: string) {
    enterTenantView(tenantId);
    void navigate({ to: '/' });
  }

  if (!isSuperadmin) {
    return (
      <div className={styles.page}>
        <div className={styles.notSuperadmin}>
          <ShieldAlert size={32} aria-hidden style={{ marginBottom: 12 }} />
          <p>
            La gestión de clientes es solo para superadmins.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">Clientes</h1>
        </div>
        <div className="page-header__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setCreating(true)}
          >
            <Plus size={16} aria-hidden />
            Crear cliente
          </button>
        </div>
      </header>

      {tenantsQ.isLoading && (
        <div className="empty-state">Cargando clientes…</div>
      )}

      {!tenantsQ.isLoading && allTenants.length === 0 && (
        <div className="empty-state">
          No hay clientes todavía. Crea el primero con el botón arriba.
        </div>
      )}

      {allTenants.length > 0 && (
        <div className={styles.toolbar}>
          <div className="subtabs" role="tablist" aria-label="Filtrar por tipo de cliente">
            {TYPE_TABS.filter((tab) => tab.id === 'todos' || counts[tab.id] > 0).map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={typeFilter === tab.id}
                className={`subtab ${typeFilter === tab.id ? 'subtab--active' : ''}`}
                onClick={() => setTypeFilter(tab.id)}
              >
                {tab.label}
                <span className={styles.tabCount}>{counts[tab.id]}</span>
              </button>
            ))}
          </div>
          <div className={styles.searchBox}>
            <Search size={14} aria-hidden />
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Buscar por nombre o dominio…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
      )}

      {allTenants.length > 0 && visible.length === 0 && (
        <div className="empty-state">
          Ningún cliente coincide con el filtro.
          {query && (
            <>
              {' '}
              <button type="button" className="btn btn--ghost" onClick={() => setQuery('')}>
                Limpiar búsqueda
              </button>
            </>
          )}
        </div>
      )}

      {visible.length > 0 && (
        <>
          {/* Desktop: tabla. Móvil: cards (la tabla corta Entrar/Configurar). */}
          <table className={`${styles.table} ${styles.tableDesktop}`}>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Plan</th>
                <th>Estado</th>
                <th>CMS configurado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div className={styles.tenantCell}>
                      <div className={styles.tenantCell__icon}>
                        <Building2 size={14} aria-hidden />
                      </div>
                      <div>
                        <div className={styles.tenantCell__name}>
                          <Tooltip content={<>slug: <code>{t.slug}</code></>}>
                            <span>{t.name}</span>
                          </Tooltip>
                        </div>
                        {t.domain ? (
                          <div className={styles.tenantCell__domain}>
                            <Globe size={11} aria-hidden />
                            {t.domain}
                          </div>
                        ) : (
                          <div className={styles.tenantCell__slug}>
                            slug: <code>{t.slug}</code>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <Tooltip
                      content={
                        <div className={styles.planTip}>
                          {CAPABILITY_KEYS.filter((c) => tenantHasCapability(t, c))
                            .map((c) => CAPABILITY_LABELS[c].replace(/\s*\(.*\)$/, ''))
                            .join(' · ')}
                        </div>
                      }
                    >
                      <span
                        className={`${styles.badge} ${
                          styles[`badge__${t.plan}`] ?? styles.badge__custom
                        }`}
                      >
                        {planLabel(t.plan)}
                      </span>
                    </Tooltip>
                  </td>
                  <td>
                    {t.active ? (
                      <span className={styles.statusActive}>
                        <CheckCircle2 size={12} aria-hidden /> Activo
                      </span>
                    ) : (
                      <span className={styles.statusInactive}>Inactivo</span>
                    )}
                  </td>
                  <td>
                    {t.cms_repo_url ? (
                      <span className={styles.cmsConfigured}>
                        <CheckCircle2 size={12} aria-hidden /> Sí
                      </span>
                    ) : (
                      <span className={styles.cmsNotConfigured}>
                        <CircleDashed size={12} aria-hidden /> No
                      </span>
                    )}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <button
                        type="button"
                        className={styles.enterButton}
                        onClick={() => handleEnterTenant(t.id)}
                        title={`Operar como ${t.name}`}
                      >
                        <LogIn size={12} aria-hidden />
                        Entrar
                      </button>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() =>
                          void navigate({ to: '/clientes/$id', params: { id: t.id } })
                        }
                        title="Configurar"
                        aria-label="Configurar cliente"
                      >
                        <Settings2 size={14} aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <ul className={styles.cardList} aria-label="Lista de clientes">
            {visible.map((t) => (
              <li key={t.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <div className={styles.tenantCell}>
                    <div className={styles.tenantCell__icon}>
                      <Building2 size={14} aria-hidden />
                    </div>
                    <div className={styles.cardIdentity}>
                      <div className={styles.tenantCell__name}>{t.name}</div>
                      {t.domain ? (
                        <div className={styles.tenantCell__domain}>
                          <Globe size={11} aria-hidden />
                          {t.domain}
                        </div>
                      ) : (
                        <div className={styles.tenantCell__slug}>
                          slug: <code>{t.slug}</code>
                        </div>
                      )}
                    </div>
                  </div>
                  <span
                    className={`${styles.badge} ${
                      styles[`badge__${t.plan}`] ?? styles.badge__custom
                    }`}
                  >
                    {planLabel(t.plan)}
                  </span>
                </div>
                <div className={styles.cardMeta}>
                  {t.active ? (
                    <span className={styles.statusActive}>
                      <CheckCircle2 size={12} aria-hidden /> Activo
                    </span>
                  ) : (
                    <span className={styles.statusInactive}>Inactivo</span>
                  )}
                  {t.cms_repo_url ? (
                    <span className={styles.cmsConfigured}>
                      <CheckCircle2 size={12} aria-hidden /> CMS
                    </span>
                  ) : (
                    <span className={styles.cmsNotConfigured}>
                      <CircleDashed size={12} aria-hidden /> Sin CMS
                    </span>
                  )}
                </div>
                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={styles.enterButton}
                    onClick={() => handleEnterTenant(t.id)}
                  >
                    <LogIn size={14} aria-hidden />
                    Entrar
                  </button>
                  <button
                    type="button"
                    className={styles.configButton}
                    onClick={() =>
                      void navigate({ to: '/clientes/$id', params: { id: t.id } })
                    }
                  >
                    <Settings2 size={14} aria-hidden />
                    Configurar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {creating && <TenantWizard onClose={() => setCreating(false)} />}
    </div>
  );
}

// ── Wizard de crear tenant + primer admin ─────────────────────────────

type WizardStep = 'tenant' | 'admin' | 'done';

function TenantWizard({ onClose }: { onClose: () => void }) {
  const create = useCreateTenantWithAdmin();
  const [step, setStep] = useState<WizardStep>('tenant');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateTenantWithAdminResult | null>(null);

  const [tenant, setTenant] = useState<TenantInput>({
    name: '',
    slug: '',
    domain: '',
    plan: 'sistema-focus',
    region: 'CL',
    timezone: 'America/Santiago',
    capabilities: presetCapabilities('sistema-focus'),
    active: true,
    cms_repo_url: '',
    cms_repo_branch: '',
    cms_github_token_env: '',
    cms_github_pat: '',
    cms_image_optimize: true,
  });

  const [createAdmin, setCreateAdmin] = useState(true);
  const [admin, setAdmin] = useState<FirstAdminInput>(() => ({
    email: '',
    name: '',
    password: generatePassword(),
    role: 'client_admin',
  }));

  const submitting = create.isPending;

  function patchTenant(p: Partial<TenantInput>) {
    setTenant((t) => ({ ...t, ...p }));
  }
  function patchAdmin(p: Partial<FirstAdminInput>) {
    setAdmin((a) => ({ ...a, ...p }));
  }

  function autoSlug(name: string) {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function handleNameChange(value: string) {
    setTenant((t) => ({
      ...t,
      name: value,
      slug: t.slug || autoSlug(value),
    }));
  }

  async function submitWizard(includeAdmin: boolean) {
    setError(null);
    if (!tenant.name.trim() || !tenant.slug.trim()) {
      setError('Nombre y slug son obligatorios.');
      setStep('tenant');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(tenant.slug)) {
      setError('Slug solo puede tener minúsculas, números y guiones.');
      setStep('tenant');
      return;
    }
    if (includeAdmin) {
      if (!admin.email.trim() || !admin.email.includes('@')) {
        setError('Email del admin inválido.');
        return;
      }
      if (admin.password.length < 8) {
        setError('La password debe tener mínimo 8 caracteres.');
        return;
      }
    }

    try {
      const res = await create.mutateAsync({
        tenant,
        admin: includeAdmin ? admin : null,
      });
      setResult(res);
      setStep('done');
    } catch (err) {
      const withResult = err as Error & {
        result?: CreateTenantWithAdminResult;
      };
      if (withResult.result) {
        setResult(withResult.result);
        setError(withResult.message);
        setStep('done');
      } else {
        setError(err instanceof Error ? err.message : 'Error al crear cliente');
      }
    }
  }

  return (
    <div className={styles.modalBackdrop} onClick={step === 'done' ? undefined : onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modal__header}>
          <h2>
            {step === 'done' ? 'Cliente creado' : 'Crear cliente'}
          </h2>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={16} aria-hidden />
          </button>
        </div>

        {step !== 'done' && (
          <WizardSteps current={step} createAdmin={createAdmin} />
        )}

        <div className={styles.modal__body}>
          {step === 'tenant' && (
            <WizardTenantStep
              form={tenant}
              onChange={patchTenant}
              onNameChange={handleNameChange}
            />
          )}
          {step === 'admin' && (
            <WizardAdminStep
              createAdmin={createAdmin}
              setCreateAdmin={setCreateAdmin}
              admin={admin}
              onChange={patchAdmin}
            />
          )}
          {step === 'done' && result && (
            <WizardDoneStep result={result} adminError={error} />
          )}

          {error && step !== 'done' && (
            <div className={styles.error}>{error}</div>
          )}
        </div>

        <div className={styles.modal__footer}>
          <div className={styles.modal__footerLeft}>
            {step === 'admin' && (
              <button
                type="button"
                className={styles.buttonGhost}
                onClick={() => {
                  setError(null);
                  setStep('tenant');
                }}
                disabled={submitting}
              >
                <ArrowLeft size={14} aria-hidden /> Atrás
              </button>
            )}
          </div>
          <div className={styles.modal__footerRight}>
            {step === 'tenant' && (
              <>
                <button
                  type="button"
                  className={styles.buttonGhost}
                  onClick={onClose}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => {
                    setError(null);
                    if (!tenant.name.trim() || !tenant.slug.trim()) {
                      setError('Nombre y slug son obligatorios.');
                      return;
                    }
                    if (!/^[a-z0-9-]+$/.test(tenant.slug)) {
                      setError('Slug solo puede tener minúsculas, números y guiones.');
                      return;
                    }
                    setStep('admin');
                  }}
                >
                  Siguiente <ArrowRight size={14} aria-hidden />
                </button>
              </>
            )}
            {step === 'admin' && (
              <button
                type="button"
                className={styles.button}
                onClick={() => submitWizard(createAdmin)}
                disabled={submitting}
              >
                {submitting
                  ? 'Creando…'
                  : createAdmin
                    ? 'Crear cliente + admin'
                    : 'Crear solo el cliente'}
              </button>
            )}
            {step === 'done' && (
              <button
                type="button"
                className={styles.button}
                onClick={onClose}
              >
                Listo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function WizardSteps({
  current,
  createAdmin,
}: {
  current: WizardStep;
  createAdmin: boolean;
}) {
  const steps: { id: WizardStep; label: string }[] = [
    { id: 'tenant', label: '1. Cliente' },
    { id: 'admin', label: createAdmin ? '2. Primer admin' : '2. Primer admin (omitir)' },
  ];
  return (
    <div className={styles.wizardSteps}>
      {steps.map((s, i) => {
        const idx = steps.findIndex((x) => x.id === current);
        const isCurrent = s.id === current;
        const isDone = i < idx;
        return (
          <div
            key={s.id}
            className={
              isCurrent
                ? `${styles.wizardStep} ${styles.wizardStep__current}`
                : isDone
                  ? `${styles.wizardStep} ${styles.wizardStep__done}`
                  : styles.wizardStep
            }
          >
            {s.label}
          </div>
        );
      })}
    </div>
  );
}

function WizardTenantStep({
  form,
  onChange,
  onNameChange,
}: {
  form: TenantInput;
  onChange: (p: Partial<TenantInput>) => void;
  onNameChange: (v: string) => void;
}) {
  const plansQ = useActivePlans();
  const plans: Plan[] = plansQ.data;
  const planOptions = ensureCompletoOption(
    plans.length > 0
      ? plans.map((p) => ({ slug: p.slug, label: p.label }))
      : PLAN_ORDER.map((slug) => ({ slug, label: PLAN_LABELS[slug] ?? slug })),
  );
  // Custom no se elige del select (se asigna al divergir caps); si el form ya
  // es custom, lo mostramos como opción seleccionable de solo lectura.
  const selectOptions =
    form.plan === CUSTOM_PLAN_SLUG
      ? [
          { slug: CUSTOM_PLAN_SLUG, label: PLAN_LABELS[CUSTOM_PLAN_SLUG] },
          ...planOptions.filter((p) => p.slug !== CUSTOM_PLAN_SLUG),
        ]
      : planOptions.filter((p) => p.slug !== CUSTOM_PLAN_SLUG);
  return (
    <>
      <fieldset className={styles.section}>
        <legend>Datos del negocio</legend>
        <Field label="Nombre del negocio" required>
          <input
            type="text"
            className={styles.input}
            value={form.name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Ej: Acme Marketing"
            autoFocus
            required
          />
        </Field>
        <Field
          label="Slug"
          required
          hint="Identificador en URLs y en el sistema. Se sugiere desde el nombre — ajústalo si quieres."
        >
          <input
            type="text"
            className={styles.input}
            value={form.slug}
            onChange={(e) => onChange({ slug: e.target.value })}
            pattern="^[a-z0-9-]+$"
            placeholder="acme-marketing"
            required
          />
        </Field>
        <Field
          label="Dominio (opcional)"
          hint="Solo informativo. Sitio del cliente: misitio.com"
        >
          <input
            type="text"
            className={styles.input}
            value={form.domain ?? ''}
            onChange={(e) => onChange({ domain: e.target.value })}
          />
        </Field>
        <div className={styles.row2}>
          <Field
            label="Plan (preset)"
            tip="El plan solo aplica un set de capacidades. La fuente de verdad del acceso son las capabilities. Si las editas a mano y no coinciden con un preset, el plan pasa a Custom."
          >
            <select
              className={styles.select}
              value={form.plan}
              onChange={(e) => {
                const p = e.target.value as TenantInput['plan'];
                onChange({ plan: p, capabilities: presetCapabilities(p, plans) });
              }}
            >
              {selectOptions.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Región"
            tip={`La moneda se deriva de la región y no es editable: ${REGION_CURRENCY[form.region]}. Todos los montos del cliente van en esa moneda.`}
          >
            <select
              className={styles.select}
              value={form.region}
              onChange={(e) => {
                const r = e.target.value as TenantInput['region'];
                const wasDefault = !form.timezone || form.timezone === defaultTimezone(form.region);
                onChange(wasDefault ? { region: r, timezone: defaultTimezone(r) } : { region: r });
              }}
            >
              {REGION_ORDER.map((r) => (
                <option key={r} value={r}>
                  {REGION_LABELS[r]}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Zona horaria"
            tip="Hora local del negocio para los recordatorios de tareas (por vencer 8am, vencidas 9am). El sistema convierte a la hora del servidor."
          >
            <select
              className={styles.select}
              value={form.timezone || defaultTimezone(form.region)}
              onChange={(e) => onChange({ timezone: e.target.value })}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Estado">
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => onChange({ active: e.target.checked })}
              />
              <span>Activo</span>
            </label>
          </Field>
        </div>
        <Field
          label="Capacidades"
          tip="Se marcan según el plan. Completo prende todas. Puedes ajustarlas antes de crear."
        >
          <CapabilityPicker
            capabilities={form.capabilities}
            plans={plans}
            onChange={(next) => onChange(next)}
          />
        </Field>
      </fieldset>

      <details className={styles.advanced}>
        <summary className={styles.advanced__summary}>
          CMS — repo del sitio Astro (opcional, configurable después)
        </summary>
        <div className={styles.advanced__body}>
          <Field
            label="URL del repo (HTTPS)"
            tip={
              <>
                URL HTTPS del repo en GitHub donde vive el sitio Astro. Ej:{' '}
                <code>https://github.com/aldeacms/lifefocus-agency.git</code>.
                Déjalo en blanco si todavía no existe — se puede configurar
                desde Editar.
              </>
            }
          >
            <input
              type="url"
              className={styles.input}
              value={form.cms_repo_url ?? ''}
              onChange={(e) => onChange({ cms_repo_url: e.target.value })}
              placeholder="https://github.com/owner/repo.git"
            />
          </Field>
          <Field label="Branch" hint="Default: main">
            <input
              type="text"
              className={styles.input}
              value={form.cms_repo_branch ?? ''}
              onChange={(e) => onChange({ cms_repo_branch: e.target.value })}
              placeholder="main"
            />
          </Field>
          <GitHubPatField
            value={form.cms_github_pat ?? ''}
            onChange={(v) => onChange({ cms_github_pat: v })}
            repoUrl={form.cms_repo_url ?? ''}
          />
          <Field
            label="Optimización de imágenes"
            tip={
              <>
                Si está activado, el CMS hace <strong>resize a 1400px</strong> y
                convierte a <strong>WebP</strong> al subir. Recomendado para
                tenants no técnicos.
              </>
            }
          >
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.cms_image_optimize ?? true}
                onChange={(e) =>
                  onChange({ cms_image_optimize: e.target.checked })
                }
              />
              <span>Optimizar imágenes automáticamente</span>
            </label>
          </Field>
        </div>
      </details>
    </>
  );
}

function WizardAdminStep({
  createAdmin,
  setCreateAdmin,
  admin,
  onChange,
}: {
  createAdmin: boolean;
  setCreateAdmin: (v: boolean) => void;
  admin: FirstAdminInput;
  onChange: (p: Partial<FirstAdminInput>) => void;
}) {
  return (
    <fieldset className={styles.section}>
      <legend>Primer usuario del cliente</legend>
      <p className={styles.sectionHint}>
        El primer admin del cliente. Queda verificado y puede entrar de
        inmediato con la password generada. Después él puede cambiarla desde
        "Olvidé mi contraseña".
      </p>

      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={createAdmin}
          onChange={(e) => setCreateAdmin(e.target.checked)}
        />
        <span>Crear el primer admin ahora</span>
      </label>

      {createAdmin && (
        <>
          <Field label="Email" required>
            <input
              type="email"
              className={styles.input}
              value={admin.email}
              onChange={(e) => onChange({ email: e.target.value })}
              placeholder="cliente@empresa.com"
              autoFocus
              required
            />
          </Field>
          <Field
            label="Nombre (opcional)"
            hint="Si lo dejas vacío, usa la parte antes del @ del email."
          >
            <input
              type="text"
              className={styles.input}
              value={admin.name ?? ''}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="Ej: María González"
            />
          </Field>
          <Field label="Rol">
            <select
              className={styles.select}
              value={admin.role}
              onChange={(e) =>
                onChange({ role: e.target.value as FirstAdminInput['role'] })
              }
            >
              <option value="client_admin">{ROLE_LABELS.client_admin} (acceso completo)</option>
              <option value="sales_rep">{ROLE_LABELS.sales_rep} (operativo)</option>
            </select>
            <small style={{ color: 'var(--color-text-subtle)', fontSize: 'var(--font-size-meta, 12px)', lineHeight: 1.4, display: 'block', marginTop: 4 }}>
              {ROLE_DESCRIPTIONS[admin.role]}
            </small>
          </Field>
          <Field
            label="Password inicial"
            hint="Cópiala — solo se vuelve a mostrar en el paso final."
          >
            <div className={styles.passwordRow}>
              <input
                type="text"
                className={styles.input}
                value={admin.password}
                onChange={(e) => onChange({ password: e.target.value })}
                minLength={8}
                required
              />
              <button
                type="button"
                className={styles.buttonGhost}
                onClick={() => onChange({ password: generatePassword() })}
                title="Regenerar password"
              >
                <RotateCcw size={14} aria-hidden /> Regenerar
              </button>
            </div>
          </Field>
        </>
      )}
    </fieldset>
  );
}

function WizardDoneStep({
  result,
  adminError,
}: {
  result: CreateTenantWithAdminResult;
  adminError: string | null;
}) {
  const captureKey = (result.tenant as Tenant & { public_capture_key?: string })
    .public_capture_key;
  return (
    <div className={styles.doneBlock}>
      <div className={styles.doneSummary}>
        <CheckCircle2 size={20} aria-hidden className={styles.doneSummary__icon} />
        <div>
          <div className={styles.doneSummary__title}>
            Cliente <strong>{result.tenant.name}</strong> creado.
          </div>
          <div className={styles.doneSummary__sub}>
            slug: <code>{result.tenant.slug}</code>
          </div>
        </div>
      </div>

      {adminError && (
        <div className={styles.error}>{adminError}</div>
      )}

      {result.admin && result.generatedPassword && (
        <div className={styles.credBox}>
          <div className={styles.credBox__header}>
            <UserPlus size={14} aria-hidden /> Credenciales del admin
          </div>
          <CredRow label="Email" value={result.admin.email} />
          <CredRow
            label="Password"
            value={result.generatedPassword}
            mono
            warn="Solo se muestra una vez — cópiala ahora."
          />
          <CredRow
            label="URL de login"
            value={loginUrl()}
          />
        </div>
      )}

      {captureKey && (
        <div className={styles.credBox}>
          <div className={styles.credBox__header}>
            <KeyRound size={14} aria-hidden /> Public capture key
          </div>
          <CredRow
            label="pk_…"
            value={captureKey}
            mono
            warn="Pégala en el sitio del cliente para capturar oportunidades."
          />
        </div>
      )}

      {!result.admin && (
        <div className={styles.notice}>
          No creaste un admin todavía. Puedes hacerlo desde{' '}
          <strong>Equipo</strong> seleccionando este cliente en el switcher.
        </div>
      )}
    </div>
  );
}

function CredRow({
  label,
  value,
  mono,
  warn,
}: {
  label: string;
  value: string;
  mono?: boolean;
  warn?: string;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }
  return (
    <div className={styles.credRow}>
      <div className={styles.credRow__label}>{label}</div>
      <div className={styles.credRow__valueWrap}>
        <code className={mono ? styles.credRow__mono : styles.credRow__value}>
          {value}
        </code>
        <button
          type="button"
          className={styles.iconButton}
          onClick={copy}
          aria-label={`Copiar ${label}`}
          title={`Copiar ${label}`}
        >
          {copied ? (
            <CheckCircle2 size={14} aria-hidden />
          ) : (
            <Copy size={14} aria-hidden />
          )}
        </button>
      </div>
      {warn && <div className={styles.credRow__warn}>{warn}</div>}
    </div>
  );
}

function loginUrl(): string {
  if (typeof window !== 'undefined') return `${window.location.origin}/login`;
  return '/login';
}

function generatePassword(): string {
  const chars =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  let out = '';
  const arr = new Uint32Array(14);
  crypto.getRandomValues(arr);
  for (let i = 0; i < arr.length; i++) out += chars[arr[i] % chars.length];
  return out;
}


// ── Field wrapper ──────────────────────────────────────────────────────

// Contenido del tooltip del label "PAT de GitHub". Antes era un callout azul
// permanente; ahora vive en el tooltip (skill focus-ui-density: footnotes/
// disclaimers → tooltips).
function GitHubPatTip() {
  return (
    <div>
      <strong>Cómo crear el PAT</strong> (la primera vez por tenant):
      <ol style={{ margin: '6px 0 0', paddingLeft: 16 }}>
        <li>
          Abrir{' '}
          <a
            href="https://github.com/settings/personal-access-tokens/new"
            target="_blank"
            rel="noopener noreferrer"
          >
            github.com/settings/personal-access-tokens/new
          </a>{' '}
          (fine-grained token).
        </li>
        <li>
          <strong>Token name</strong>: <code>focus-publisher-&lt;tenant&gt;</code>.
          <strong> Expiration</strong>: 1 año.
        </li>
        <li>
          <strong>Repository access</strong>: <em>Only select repositories</em>{' '}
          → elegir el repo del cliente.
        </li>
        <li>
          <strong>Repository permissions → Contents</strong>:{' '}
          <em>Read and write</em>. <em>Metadata: Read-only</em>. GitHub a veces
          deja Contents en Read-only por defecto — verificar antes de guardar.
        </li>
        <li>
          Generar el token, copiarlo y pegarlo en el campo. Usa{' '}
          <em>Probar</em> para validar.
        </li>
      </ol>
    </div>
  );
}

export function Field({
  label,
  required,
  hint,
  tip,
  children,
  htmlFor,
  hintId,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  /** Contenido para el tooltip al lado del label (icono Info). */
  tip?: React.ReactNode;
  children: React.ReactNode;
  htmlFor?: string;
  hintId?: string;
}) {
  return (
    <div className={styles.field}>
      <label className={styles.field__label} htmlFor={htmlFor}>
        {label}
        {required && <span className={styles.field__required}>*</span>}
        {tip && (
          <Tooltip content={tip}>
            <span className={styles.field__tipIcon} aria-label="Más información">
              <Info size={12} aria-hidden />
            </span>
          </Tooltip>
        )}
      </label>
      {children}
      {hint && (
        <span id={hintId} className={styles.field__hint}>
          {hint}
        </span>
      )}
    </div>
  );
}

// ── GitHubPatField ─────────────────────────────────────────────────────
//
// Input password-type para el PAT + botón "Probar" que valida contra
// GitHub API antes de guardar. Reusado en wizard y editar tenant.
//
// El PAT viaja al campo `hidden:true` `cms_github_pat` del tenant. PB no lo
// devuelve al cargar, así que en modo "edit" el input arranca vacío y un hint
// debajo aclara "Ya hay un PAT configurado. Deja vacío para no cambiarlo."

interface PatTestState {
  status: 'idle' | 'testing' | 'ok' | 'error';
  message?: string;
}

export function GitHubPatField({
  value,
  onChange,
  repoUrl,
  hasExisting,
}: {
  value: string;
  onChange: (v: string) => void;
  repoUrl: string;
  hasExisting?: boolean;
}) {
  const [test, setTest] = useState<PatTestState>({ status: 'idle' });

  async function probe() {
    setTest({ status: 'testing' });
    const pat = value.trim();
    if (!pat) {
      setTest({ status: 'error', message: 'Pega el PAT primero.' });
      return;
    }
    const ownerRepo = parseGithubRepo(repoUrl);
    if (!ownerRepo) {
      setTest({
        status: 'error',
        message: 'URL del repo inválida. Debe ser https://github.com/owner/repo(.git).',
      });
      return;
    }
    try {
      // GitHub Contents API: si el PAT tiene Contents:write sobre el repo,
      // este GET devuelve 200 con info del repo. 404/403 = sin permisos.
      const res = await fetch(`https://api.github.com/repos/${ownerRepo}`, {
        headers: {
          Authorization: `Bearer ${pat}`,
          Accept: 'application/vnd.github+json',
        },
      });
      if (res.ok) {
        const data = (await res.json()) as { full_name?: string };
        setTest({
          status: 'ok',
          message: `OK · acceso a ${data.full_name ?? ownerRepo}. Guarda el cliente para aplicarlo.`,
        });
        return;
      }
      if (res.status === 401) {
        setTest({ status: 'error', message: 'PAT inválido o revocado (401).' });
      } else if (res.status === 404) {
        setTest({
          status: 'error',
          message: 'Repo no encontrado o el PAT no tiene acceso (404).',
        });
      } else {
        setTest({
          status: 'error',
          message: `GitHub respondió ${res.status}.`,
        });
      }
    } catch (err) {
      setTest({
        status: 'error',
        message: err instanceof Error ? err.message : 'Error de red al probar el PAT.',
      });
    }
  }

  return (
    <Field
      label="PAT de GitHub"
      tip={<GitHubPatTip />}
      hint={
        hasExisting && !value
          ? 'PAT configurado — déjalo vacío para no cambiarlo.'
          : undefined
      }
    >
      <div className={styles.patRow}>
        {/* SecretInput usa type="text" + CSS para ocultar caracteres en vez
            de type="password", así los password managers (Comet/1Password/
            Bitwarden/Keychain) no lo detectan e interceptan al submit. Fix
            del bug 2026-05-25/26 donde el PAT pegado se borraba antes de
            llegar al PATCH. Memoria feedback_wizard_field_password_not_persisted. */}
        <SecretInput
          className={styles.input}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (test.status !== 'idle') setTest({ status: 'idle' });
          }}
          placeholder={hasExisting ? '••••••••  (oculto)' : 'github_pat_…'}
        />
        <button
          type="button"
          className={styles.patBtn}
          onClick={probe}
          disabled={test.status === 'testing' || !value.trim()}
        >
          {test.status === 'testing' ? 'Probando…' : 'Probar'}
        </button>
      </div>
      {test.status === 'ok' && (
        <span className={`${styles.patStatus} ${styles.patStatusOk}`}>
          <CheckCircle2 size={12} aria-hidden /> {test.message}
        </span>
      )}
      {test.status === 'error' && (
        <span className={`${styles.patStatus} ${styles.patStatusErr}`}>
          <ShieldAlert size={12} aria-hidden /> {test.message}
        </span>
      )}
    </Field>
  );
}

/** Extrae "owner/repo" de "https://github.com/owner/repo(.git)" o similar. */
function parseGithubRepo(url: string): string | null {
  if (!url) return null;
  const m = url.match(/^https:\/\/github\.com\/([^/]+)\/([^/]+?)(?:\.git)?\/?$/);
  if (!m) return null;
  return `${m[1]}/${m[2]}`;
}
